import React, { useMemo, useState } from 'react';
import { MessageSquarePlus, Bug, Lightbulb, Heart, Download, ChevronDown } from 'lucide-react';
import { formatLastActive } from './_utils';
import { downloadFeedbackCSV } from '../../lib/csvExport';

/**
 * FeedbackInbox — founder-facing list of in-app beta feedback submitted from
 * the floating FeedbackWidget. Collapsible (Visual Minimalism), filterable by
 * status, with a per-note status cycle (new → triaged → resolved) and a
 * one-click CSV export for roadmap sourcing.
 *
 * Props:
 *   feedback: array of { id, category, message, page, user_email, user_agent, status, created_at }
 *   onSetStatus: (id, status) => void
 *   busyId: id currently being updated (disables its pill)
 */
const CATEGORY_META = {
  bug: { label: 'Bug', icon: Bug, cls: 'text-enf-role-warning' },
  idea: { label: 'Idea', icon: Lightbulb, cls: 'text-enf-role-info' },
  love: { label: 'Love it', icon: Heart, cls: 'text-enf-accent-fg' },
};

const STATUS_ORDER = ['new', 'triaged', 'resolved'];
const STATUS_CLS = {
  new: 'bg-enf-accent/15 text-enf-accent-fg border-enf-accent-fg/40',
  triaged: 'bg-enf-role-info/15 text-enf-role-info border-enf-role-info/40',
  resolved: 'bg-enf-surface-mid text-enf-text-muted border-enf-surface-border',
};

const FILTERS = ['all', 'new', 'triaged', 'resolved'];

const FeedbackInbox = ({ feedback = [], onSetStatus, busyId }) => {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('all');

  const counts = useMemo(() => {
    const c = { all: feedback.length, new: 0, triaged: 0, resolved: 0 };
    feedback.forEach((f) => { c[f.status] = (c[f.status] || 0) + 1; });
    return c;
  }, [feedback]);

  const visible = useMemo(
    () => (filter === 'all' ? feedback : feedback.filter((f) => f.status === filter)),
    [feedback, filter],
  );

  const nextStatus = (s) => STATUS_ORDER[(STATUS_ORDER.indexOf(s) + 1) % STATUS_ORDER.length];

  return (
    <div className="bg-enf-surface-deep border border-enf-surface-border rounded-xl p-6 mb-10" data-testid="admin-feedback-card">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between"
        data-testid="admin-feedback-toggle"
      >
        <div className="flex items-center gap-2">
          <MessageSquarePlus className="w-4 h-4 text-enf-accent-fg" />
          <div className="text-sm font-semibold uppercase tracking-widest text-enf-text-secondary">Beta feedback</div>
          {counts.new > 0 && (
            <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full bg-enf-accent/15 text-enf-accent-fg border border-enf-accent-fg/40">
              {counts.new} new
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-enf-text-muted" data-testid="admin-feedback-count">{counts.all} total</span>
          <ChevronDown className={`w-4 h-4 text-enf-text-muted transition-transform ${open ? 'rotate-180' : ''}`} />
        </div>
      </button>

      {open && (
        <div className="mt-5">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
            <div className="flex flex-wrap gap-1.5">
              {FILTERS.map((f) => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${
                    filter === f
                      ? 'bg-enf-accent border-enf-accent-fg text-enf-text-primary'
                      : 'bg-enf-surface-mid border-enf-surface-border text-enf-text-secondary hover:text-enf-text-primary'
                  }`}
                  data-testid={`feedback-filter-${f}`}
                >
                  {f[0].toUpperCase() + f.slice(1)} ({counts[f] || 0})
                </button>
              ))}
            </div>
            <button
              onClick={() => downloadFeedbackCSV(feedback)}
              disabled={feedback.length === 0}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold bg-enf-surface-mid hover:bg-enf-surface-elevated border border-enf-surface-border disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              data-testid="feedback-export-csv"
            >
              <Download className="w-3.5 h-3.5" /> Export CSV
            </button>
          </div>

          {visible.length === 0 ? (
            <div className="text-sm text-enf-text-muted py-6 text-center">
              {feedback.length === 0
                ? 'No feedback yet. Notes land here when a tester uses the in-app Feedback button.'
                : `No ${filter} feedback.`}
            </div>
          ) : (
            <div className="space-y-3">
              {visible.map((f) => {
                const meta = CATEGORY_META[f.category] || { label: f.category || 'Note', icon: MessageSquarePlus, cls: 'text-enf-text-muted' };
                const Icon = meta.icon;
                return (
                  <div key={f.id} className="bg-enf-surface-mid border border-enf-surface-border rounded-lg p-4" data-testid={`feedback-row-${f.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`inline-flex items-center gap-1 text-xs font-semibold ${meta.cls}`}>
                            <Icon className="w-3.5 h-3.5" /> {meta.label}
                          </span>
                          <span className="text-xs text-enf-text-muted">·</span>
                          <span className="text-xs text-enf-text-muted truncate">{f.user_email || 'unknown'}</span>
                        </div>
                        <div className="text-sm text-enf-text-primary mt-2 whitespace-pre-wrap leading-relaxed border-l-2 border-enf-surface-border pl-3">
                          {f.message}
                        </div>
                        <div className="text-[11px] text-enf-text-muted mt-2 flex flex-wrap gap-x-3 gap-y-1">
                          {f.page && <span><span className="text-enf-text-muted">Page:</span> {f.page}</span>}
                          {f.created_at && <span>{formatLastActive(f.created_at)}</span>}
                        </div>
                      </div>
                      <button
                        onClick={() => onSetStatus && onSetStatus(f.id, nextStatus(f.status || 'new'))}
                        disabled={busyId === f.id}
                        title="Click to cycle status"
                        className={`shrink-0 text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full border transition-colors disabled:opacity-50 ${STATUS_CLS[f.status] || STATUS_CLS.new}`}
                        data-testid={`feedback-status-${f.id}`}
                      >
                        {busyId === f.id ? '…' : (f.status || 'new')}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FeedbackInbox;
